import React, { useState, useEffect, useCallback, useRef } from "react";
import { Container, Header, TaskList, TaskItem, StatusMessage } from "./styles";
import { ModalOverlay, ModalContainer, ModalHeader, ModalBody, ModalFooter, ModalButton } from "../ModalShared";
import { GoCheck, GoSync, GoX, GoDatabase, GoFile, GoPackage } from "react-icons/go";
import { FiLoader, FiCheckCircle, FiAlertCircle, FiClock, FiWifi, FiWifiOff, FiX, FiRefreshCw, FiTerminal } from "react-icons/fi";
import authService from "../../../../services/authService";
import api from "../../../../services/api";
import WorkerLogTerminal from "../../../../components/WorkerLogTerminal";

// Etapas do SPED ICMS/IPI (na ordem do SpedConfigs.js)
const ICMS_TASKS = [
  { id: "0000", name: "0000 - Abertura do Arquivo", description: "Dados da empresa e período" },
  { id: "0150", name: "0150 - Fornecedores/Participantes", description: "Cadastro de fornecedores" },
  { id: "0200", name: "0200 - Produtos/Itens", description: "Cadastro de produtos" },
  { id: "C100", name: "C100 - Documentos Fiscais", description: "NF-e, NFC-e, etc." },
  { id: "C500", name: "C500 - Energia/Gás", description: "Documentos de energia" },
  { id: "D100", name: "D100 - Transportes", description: "CT-e e documentos de frete" },
  { id: "E110", name: "E110 - Apuração ICMS", description: "Apuração do imposto" },
  { id: "C170", name: "C170 - Itens Documentos", description: "Itens das notas fiscais" },
  { id: "C190", name: "C190 - Consolidação C100", description: "Consolidação por CST" },
  { id: "D190", name: "D190 - Consolidação D100", description: "Consolidação de transportes" },
  { id: "H005", name: "H005 - Inventário", description: "Totais do inventário" },
  { id: "H010", name: "H010 - Itens Inventário", description: "Itens do inventário" },
  // Etapas de verificação de XMLs físicos
  { id: "VERIFY_C100", name: "Verificação NFe (C100)", description: "Verificando XMLs físicos" },
  { id: "VERIFY_D100", name: "Verificação CTe (D100)", description: "Verificando XMLs físicos" },
];

// Etapas do SPED PIS/COFINS
const PIS_TASKS = [
  { id: "0000", name: "0000 - Abertura", description: "Dados da empresa" },
  { id: "0150", name: "0150 - Participantes", description: "Cadastro de participantes" },
  { id: "0200", name: "0200 - Produtos", description: "Cadastro de produtos" },
  { id: "C100", name: "C100 - Docs Fiscais", description: "Documentos fiscais" },
  { id: "C500", name: "C500 - Energia/Gás", description: "Documentos de energia" },
  { id: "D100", name: "D100 - Transportes", description: "Documentos de transporte" },
  { id: "F100", name: "F100 - Imobiliária", description: "Operações imobiliárias" },
  { id: "F525", name: "F525 - Receitas Caixa", description: "Receitas por caixa" },
  { id: "M100", name: "M100 - Crédito PIS", description: "Créditos de PIS" },
  { id: "M105", name: "M105 - Det. Crédito PIS", description: "Detalhamento créditos" },
  { id: "M200", name: "M200 - Apuração PIS", description: "Apuração PIS" },
  { id: "M205", name: "M205 - Contrib. PIS", description: "Contribuição PIS" },
  { id: "M500", name: "M500 - Crédito COFINS", description: "Créditos COFINS" },
  { id: "M600", name: "M600 - Apuração COFINS", description: "Apuração COFINS" },
  { id: "C170", name: "C170 - Itens", description: "Itens dos documentos" },
  // Etapas de verificação de XMLs físicos
  { id: "VERIFY_C100", name: "Verificação NFe (C100)", description: "Verificando XMLs físicos" },
  { id: "VERIFY_D100", name: "Verificação CTe (D100)", description: "Verificando XMLs físicos" },
];

// URL do WebSocket de logs
const WS_URL = process.env.REACT_APP_WS_URL || 'wss://log.vidal-app.com/ws';

const PROCESSING_TASKS = [
  { id: "UNZIP", name: "Descompactação", description: "Extraindo arquivos XML" },
  { id: "PROCESS", name: "Processamento XMLs", description: "Lendo e indexando documentos" },
  { id: "VERIFY_C100", name: "Verificação NFe (C100)", description: "Validando integridade" },
  { id: "VERIFY_D100", name: "Verificação CTe (D100)", description: "Validando integridade" },
];

// URL do WebSocket de logs
// const WS_URL = process.env.REACT_APP_WS_URL || 'wss://log.vidal-app.com/ws'; // Removed duplicate declaration

const getTaskLabel = (taskId) => {
    switch(taskId) {
        case 'UNZIP': return 'descompactados';
        case 'PROCESS': return 'indexados';
        case 'VERIFY_C100': return 'encontrados';
        case 'VERIFY_D100': return 'encontrados';
        default: return 'registros';
    }
};

const fmt = (n) => n?.toLocaleString('pt-BR');

const ProcessingScreen = ({ 
  onComplete, 
  onError, 
  projectId,
  spedType = 'xml', // 'icms', 'pis', 'xml', 'mixed'
  initialState = null // Estado inicial para restauração após reload
}) => {
  const [currentMixedPhase, setCurrentMixedPhase] = useState('icms'); // 'icms' ou 'pis' para modo mixed (ICMS processa primeiro)
  const phaseRef = useRef('icms'); // Ref para controle síncrono no callback e evitar resets múltiplos

  // Determine task list based on type
  const getTasks = () => {
      if (spedType === 'icms') return ICMS_TASKS;
      if (spedType === 'pis') return PIS_TASKS;
      if (spedType === 'mixed') {
        // No modo misto, mostramos as tarefas da fase atual
        return currentMixedPhase === 'icms' ? ICMS_TASKS : PIS_TASKS;
      }
      return PROCESSING_TASKS;
  };
  
  // Sincronizar ref com state se mudar por outros meios (embora controlemos isso manualmente)
  useEffect(() => {
     phaseRef.current = currentMixedPhase;
  }, [currentMixedPhase]);

  const tasks = getTasks();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Inicializar estados com base no initialState (se disponível)
  const getInitialTaskStates = () => {
    // Para modo mixed, sempre iniciar com ICMS_TASKS
    const initialTasks = spedType === 'mixed' ? ICMS_TASKS : tasks;
    
    if (initialState && initialState.scriptsCompletos > 0) {
      return initialTasks.map((t, idx) => {
        if (idx < initialState.scriptsCompletos) {
          return { ...t, status: 'completed', progress: 100, records: 0, duration: null };
        } else if (idx === initialState.scriptsCompletos) {
          return { ...t, status: 'active', progress: 50, records: initialState.registrosProcessados || 0, duration: null };
        }
        return { ...t, status: 'pending', progress: 0, records: 0, duration: null };
      });
    }
    return initialTasks.map(t => ({ ...t, status: 'pending', progress: 0, records: 0, duration: null }));
  };

  // Estado das tarefas
  const [taskStates, setTaskStates] = useState(getInitialTaskStates);
  
  const [globalProgress, setGlobalProgress] = useState(initialState?.progress || 0);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(initialState?.scriptsCompletos || -1);
  const [overallStatus, setOverallStatus] = useState('processing');
  const [statusMessage, setStatusMessage] = useState(initialState?.stage || 'Conectando ao servidor...');
  const [wsConnected, setWsConnected] = useState(false);
  const [totalRecords, setTotalRecords] = useState(initialState?.registrosProcessados || 0);
  const [showTerminal, setShowTerminal] = useState(false);

  // Log de restauração
  useEffect(() => {
    if (initialState) {
      console.log('[ProcessingScreen] Restored from initial state:', initialState);
    }
  }, []);

  // Helper to update a specific task by ID
  const updateTaskStatus = useCallback((taskId, updates) => {
      setTaskStates(prev => prev.map(t => 
          t.id === taskId ? { ...t, ...updates } : t
      ));
  }, []);

  const completeTask = useCallback((taskId) => {
      updateTaskStatus(taskId, { status: 'completed', progress: 100 });
  }, [updateTaskStatus]);

  // Handler para atualizações de progresso do WebSocket
  const handleProgressUpdate = useCallback((rawData) => {
    // O WebSocket server encapsula as mensagens: { success, message: {...} }
    const data = rawData.message || rawData;
    const payload = data.data || {};

    console.log('[ProcessingScreen] Received update:', data.type, payload);
    
    // --- LÓGICA PARA UPLOAD INICIAL DE SPED (antes do processamento) ---
    if (data.type === 'sped_upload_started') {
      const { stage, progress, fase } = payload;
      
      if (stage) {
        setStatusMessage(stage);
      }
      
      if (progress !== undefined) {
        setGlobalProgress(Math.round(progress));
      }
      
      // Durante o upload, mostrar status mas não marcar tarefas ainda
      if (fase === 'upload' || fase === 'analysis' || fase === 'organizing') {
        // Manter tarefas em pending durante upload
        setOverallStatus('processing');
      } else if (fase === 'queued') {
        // Arquivos foram enviados para processamento, aguardar início
        setStatusMessage(stage || 'Aguardando início do processamento...');
      }
      
      return;
    }
    
    // --- LÓGICA PARA PROCESSAMENTO DE UPLOAD (XMLs) ---
    if (spedType === 'xml') {
        if (data.type === 'unzip_upload') {
            // Fase de Unzip
            setCurrentTaskIndex(0); // UNZIP is index 0
            
            // Tenta pegar contagem de arquivos
            const totalFiles = payload.totalFiles || payload.total || 0;
            const processedFiles = payload.processedFiles || payload.processed || 0;
            
            if (payload.status === 'completed' || payload.progress >= 100) {
                 updateTaskStatus('UNZIP', { 
                     status: 'completed', 
                     progress: 100,
                     records: totalFiles || processedFiles // Persist final count
                 });
                 updateTaskStatus('PROCESS', { status: 'active', progress: 0 }); // Start next
                 setStatusMessage('Descompactação concluída. Iniciando processamento...');
            } else if (payload.status === 'error') {
                 updateTaskStatus('UNZIP', { status: 'error' });
                 setOverallStatus('error');
                 setStatusMessage(payload.stage || 'Erro na descompactação');
            } else {
                 updateTaskStatus('UNZIP', { 
                     status: 'active', 
                     progress: payload.progress,
                     records: processedFiles 
                 });
                 setStatusMessage(payload.stage || 'Descompactando...');
                 setGlobalProgress(Math.round(payload.progress * 0.15)); // Unzip vale 15% do total
            }
        } 
        else if (data.type === 'process_upload_xmls') {
            // Fase de Processamento
            // Garantir que UNZIP fique como completed se já não estiver
            // E preservar sua contagem se existir (não resetar)
            setTaskStates(prev => prev.map(t => 
                t.id === 'UNZIP' && t.status !== 'completed' ? { ...t, status: 'completed', progress: 100 } : t
            ));
            
            setCurrentTaskIndex(1); // PROCESS is index 1

            const { stage, progress, stats } = payload;
            
            // Contagens específicas
            // Se for verificação, vem em 'processados'
            // Se for processamento principal, pode vir em 'recordCount' ou 'stats.processed'
            
            if (payload.status === 'completed' || payload.stage?.includes('Concluído')) {
                // Finalização
                updateTaskStatus('PROCESS', { status: 'completed', progress: 100, records: stats?.processed || payload.recordCount || 0 });
                updateTaskStatus('VERIFY_C100', { status: 'completed', progress: 100 }); 
                updateTaskStatus('VERIFY_D100', { status: 'completed', progress: 100 });
                
                setOverallStatus('success');
                setStatusMessage('Processamento concluído!');
                setGlobalProgress(100);
                
                if (stats) setTotalRecords(stats.processed || 0);
                
                setTimeout(() => onComplete && onComplete(), 2000);
            } 
            else if (stage && stage.includes('Verificando C100')) {
                // Ao entrar em Verificação C100, o PROCESS (indexação) já acabou. 
                // Precisamos garantir que PROCESS tenha o número final de indexados.
                if (stats && stats.processed) {
                    updateTaskStatus('PROCESS', { status: 'completed', progress: 100, records: stats.processed });
                } else {
                    completeTask('PROCESS');
                }

                updateTaskStatus('VERIFY_C100', { 
                    status: 'active', 
                    progress: progress,
                    records: payload.processados || 0 // Vem do worker verify
                });
                setStatusMessage(stage);
                 setGlobalProgress(80 + (progress * 0.1));
            }
            else if (stage && stage.includes('Verificando D100')) {
                 completeTask('VERIFY_C100'); // Finaliza anterior
                 
                 // Se veio contagem final do C100 no payload anterior ou neste (não comum, mas previnimos)
                 // O ideal é persistir quando C100 finaliza.
                 
                 updateTaskStatus('VERIFY_D100', { 
                     status: 'active', 
                     progress: progress,
                     records: payload.processados || 0 
                 });
                 setStatusMessage(stage);
                 setGlobalProgress(90 + (progress * 0.1));
                 
            } else {
                // Processamento normal (Indexação)
                updateTaskStatus('PROCESS', { 
                    status: 'active', 
                    progress: progress,
                    records: stats?.processed || payload.recordCount || 0 
                });
                setStatusMessage(stage);
                setGlobalProgress(15 + (progress * 0.65)); 
            }
        }
        return; 
    }

    // --- LÓGICA PARA MODO MISTO (ICMS + PIS sequencial) ---
    if (spedType === 'mixed') {
      // Transição entre fases
      if (data.type === 'process_sped_transition') {
        const { from, to } = payload;
        console.log(`[ProcessingScreen] Phase transition: ${from} → ${to}`);
        
        if (to === 'pis' && phaseRef.current !== 'pis') {
          // Resetar tarefas para a fase PIS
          phaseRef.current = 'pis'; // Lock immediate transition
          setCurrentMixedPhase('pis');
          setTaskStates(PIS_TASKS.map(t => ({ 
            ...t, 
            status: 'pending',
            progress: 0,
            records: 0,
            duration: null 
          })));
          setStatusMessage('Iniciando processamento PIS/COFINS...');
          // Progresso global: ICMS termina em 50%, PIS começa em 50%
          setGlobalProgress(50);
        }
        return;
      }

      // Detecção de transição implícita (fallback se mensagem de transição falhar)
      // Se recebermos mensagem de PIS e NÃO estivermos na fase PIS, forçar transição
      // USANDO REF para evitar race conditions no React Update Batching
      if (data.type === 'process_sped_pis' && phaseRef.current !== 'pis') {
           console.log(`[ProcessingScreen] Detectado início do PIS (transição implícita via Ref). Fase anterior: ${phaseRef.current}`);
           console.log('[ProcessingScreen] Forçando troca de tarefas para PIS...');
           
           phaseRef.current = 'pis'; // Lock immediate transition
           setCurrentMixedPhase('pis');
           
           setTaskStates(PIS_TASKS.map(t => ({ 
             ...t, 
             status: 'pending',
             progress: 0,
             records: 0,
             duration: null 
           })));
           
           setStatusMessage('Sincronizando processamento PIS/COFINS...');
           setGlobalProgress(50);
           // Não retornamos aqui para permitir que a mensagem atual seja processada e atualize o status da tarefa PIS
      }

      // Mensagem geral do modo misto
      if (data.type === 'process_sped_mixed') {
        const { progress, etapa, summary } = payload;
        
        if (etapa === 'all_completed' || progress >= 100) {
          setOverallStatus('success');
          setGlobalProgress(100);
          setStatusMessage('Processamento misto concluído!');
          setTaskStates(prev => prev.map(task => ({
            ...task,
            status: 'completed',
            progress: 100
          })));
          setTimeout(() => onComplete && onComplete(), 2000);
        }
        return;
      }

      // Processar mensagens ICMS ou PIS conforme a fase atual
      const isIcmsMessage = data.type === 'process_sped_icms';
      const isPisMessage = data.type === 'process_sped_pis';

      if ((currentMixedPhase === 'icms' && isIcmsMessage) || (currentMixedPhase === 'pis' && isPisMessage)) {
        // Processar com lógica normal, mas ajustar progresso global
        // ICMS: 0-50%, PIS: 50-100%
        const baseProgress = currentMixedPhase === 'icms' ? 0 : 50;
        const phaseMultiplier = 0.5; // Cada fase vale 50%
        
        // Continuar para a lógica normal abaixo, mas será tratado
      }
    }

    // --- LÓGICA EXISTENTE PARA SPED ICMS/PIS ---
    // Para modo misto, também aceita ambos os tipos de mensagem
    const acceptedTypes = spedType === 'mixed' 
      ? ['process_sped_icms', 'process_sped_pis'] 
      : [spedType === 'icms' ? 'process_sped_icms' : 'process_sped_pis'];
    
    if (acceptedTypes.includes(data.type) && data.projectId === projectId) {
      const {
        stage,
        progress,
        metricas,
        etapa,
        fase
      } = payload;
      
      // Atualizar mensagem de status
      if (stage) {
        setStatusMessage(stage);
      }
      
      // Progresso global
      if (progress !== undefined) {
        setGlobalProgress(Math.round(progress));
      }
      
      // NOVO: Tratar progresso granular por chunk
      if (etapa === 'chunk_progresso' && metricas) {
        const { scriptsCompletos, scriptAtual, chunkAtual, totalChunks, registrosProcessados, registrosTotal } = metricas;
        
        if (scriptsCompletos !== undefined) {
          setCurrentTaskIndex(scriptsCompletos);
          
          // Calcular progresso dentro do chunk
          const chunkPercent = totalChunks > 0 ? (chunkAtual / totalChunks) * 100 : 50;
          
          // Atualizar estados das tarefas active/completed
          setTaskStates(prev => prev.map((task, idx) => {
            if (idx < scriptsCompletos) {
              return { ...task, status: 'completed', progress: 100 };
            } else if (idx === scriptsCompletos) {
              return { 
                ...task, 
                status: 'active',
                progress: Math.round(chunkPercent),
                records: registrosProcessados || 0
              };
            }
            return task;
          }));
        }
        
        if (scriptAtual) {
          const chunkInfo = totalChunks > 1 ? ` (chunk ${chunkAtual}/${totalChunks})` : '';
          setStatusMessage(`${scriptAtual}${chunkInfo}`);
        }
        
        if (registrosTotal) {
          setTotalRecords(registrosTotal);
        }
      }
      // NOVO: Tratar fase de verificação de XMLs
      else if (etapa === 'verificando_xmls') {
        const { verificando, processados, total, percentual } = payload;
        
        // Encontrar índice da tarefa de verificação
        const verifyTaskId = verificando?.includes('C100') ? 'VERIFY_C100' : 'VERIFY_D100';
        const verifyTaskIndex = tasks.findIndex(t => t.id === verifyTaskId);
        
        if (verifyTaskIndex >= 0) {
          // Marcar tarefas anteriores como completadas, a de verificação como ativa
          setTaskStates(prev => prev.map((task, idx) => {
            if (idx < verifyTaskIndex) {
              return { ...task, status: 'completed', progress: 100 };
            } else if (idx === verifyTaskIndex) {
              return { 
                ...task, 
                status: 'active',
                progress: percentual || 0,
                records: processados || 0
              };
            }
            return task;
          }));
          
          setCurrentTaskIndex(verifyTaskIndex);
          setStatusMessage(`Verificando ${verificando}: ${(processados || 0).toLocaleString()}/${(total || 0).toLocaleString()} (${percentual || 0}%)`);
        }
      }
      // Processar métricas detalhadas (lógica genérica para logs que não são chunk_progresso)
      else if (metricas) {
        const { scriptsTotal, scriptsCompletos, registrosTotal, scriptAtual, registrosProcessados, scriptIndex } = metricas;
        
        let currentIndex = -1;
        
        // Tentar usar scriptsCompletos primeiro (fim de etapa)
        if (scriptsCompletos !== undefined) {
          currentIndex = scriptsCompletos;
        } 
        // Fallback: usar scriptIndex (durante etapa)
        // scriptIndex é 1-based, então subtraímos 1 para pegar o índice do array (0-based)
        else if (scriptIndex !== undefined && scriptIndex > 0) {
          currentIndex = scriptIndex - 1;
        }

        if (currentIndex >= 0 && scriptsTotal) {
          // Só atualiza se o índice for válido e maior ou igual ao atual (não regredir)
          setCurrentTaskIndex(prev => Math.max(prev, currentIndex));
          
          // Tentar calcular progresso baseado em registros se disponível
          let estimatedProgress = 50; // Fallback
          if (registrosTotal > 0 && registrosProcessados !== undefined) {
             estimatedProgress = Math.round((registrosProcessados / registrosTotal) * 100);
          }
          
          // Atualizar estados das tarefas
          setTaskStates(prev => prev.map((task, idx) => {
            // Tarefas anteriores ao índice atual são completadas automaticamente
            if (idx < currentIndex) {
              return { ...task, status: 'completed', progress: 100 };
            } 
            // Tarefa atual
            else if (idx === currentIndex) {
              // Só atualizar se tivermos uma estimativa melhor ou se o status mudou para active
              const currentTaskProgress = task.progress || 0;
              // Se estimatedProgress for menor que o atual (ex: novo batch), mantém o visual suave ?
              // Melhor mostrar o real do chunk
              const newProgress = registrosProcessados ? estimatedProgress : (currentTaskProgress > 0 ? currentTaskProgress : 10);
              
              return { 
                ...task, 
                status: 'active',
                progress: newProgress,
                records: registrosTotal || task.records || 0
              };
            }
            return task;
          }));
          
          // Calcular progresso global baseado em scripts
          // Usamos currentIndex como base para quantos já foram (aproximadamente)
          const baseProgress = (currentIndex / scriptsTotal) * 100;
          const currentTaskContribution = (estimatedProgress / 100) * (1 / scriptsTotal) * 100;
           
          setGlobalProgress(Math.min(99, Math.round(baseProgress + currentTaskContribution)));
        }
        
        if (registrosTotal) {
          setTotalRecords(registrosTotal);
        }
        
        if (scriptAtual) {
          setStatusMessage(`Processando: ${scriptAtual}`);
        }
      }
      
      // Etapa de script completo
      if (etapa === 'script_completo' && metricas?.scriptsCompletos !== undefined) {
        const completedIdx = metricas.scriptsCompletos - 1;
        setTaskStates(prev => prev.map((task, idx) => {
          if (idx === completedIdx) {
            return { 
              ...task, 
              status: 'completed', 
              progress: 100,
              records: payload.registrosProcessados || task.records,
              duration: payload.duracao
            };
          }
          return task;
        }));
      }
      
      // Processamento concluído
      // Processamento concluído
      if (etapa === 'all_completed' || progress >= 100) {
        // Se estiver no modo MISTO e receber conclusão do ICMS, não finalizar ainda
        // Esperar pela conclusão do PIS ou mensagem geral do mixed
        if (spedType === 'mixed') {
           // Se a mensagem veio do canal ICMS, é apenas a fase 1 terminando
           if (data.type === 'process_sped_icms') {
             console.log('[ProcessingScreen] Fase ICMS concluída (Mixed Mode). Aguardando PIS...');
             // Marcar tarefas atuais como concluídas, mas NÃO chamar onComplete
             setTaskStates(prev => prev.map(task => ({
               ...task,
               status: 'completed',
               progress: 100
             })));
             return; 
           }
           // Se veio do PIS ou do canal mixed, aí sim pode finalizar
        }

        setOverallStatus('success');
        setGlobalProgress(100);
        setStatusMessage('Processamento concluído com sucesso!');
        
        // Marcar todas as tarefas como completadas
        setTaskStates(prev => prev.map(task => ({
          ...task,
          status: 'completed',
          progress: 100
        })));
        
        // Chamar callback após um pequeno delay para mostrar a animação final
        setTimeout(() => {
          onComplete && onComplete();
        }, 2000);
      }
      
      // Erro
      if (payload.status === 'error' || etapa === 'error') {
        setOverallStatus('error');
        setStatusMessage(payload.error || payload.message || 'Erro no processamento');
        onError && onError(payload.error);
      }
    }
  }, [projectId, spedType, tasks.length, onComplete, onError, completeTask, updateTaskStatus]);

  // Conectar ao WebSocket
  useEffect(() => {
    const connectWebSocket = () => {
      try {
        // Obter token de autenticação
        const token = authService.getToken();
        if (!token) {
          console.error('[ProcessingScreen] No auth token available');
          setStatusMessage('Erro: Token de autenticação não encontrado');
          return;
        }
        
        // Conectar com token na query string
        const wsUrlWithToken = `${WS_URL}?token=${token}`;
        console.log('[ProcessingScreen] Connecting to WebSocket:', WS_URL);
        
        const ws = new WebSocket(wsUrlWithToken);
        wsRef.current = ws;
        
        ws.onopen = () => {
          console.log('[ProcessingScreen] WebSocket connected');
          setWsConnected(true);
          setStatusMessage('Aguardando atualizações do processamento...');
          
          // Inscrever-se para receber atualizações do projeto
          
          // Subscribe to upload started messages (common for all types)
          ws.send(JSON.stringify({
            action: 'subscribe',
            type: 'sped_upload_started',
            projectId: projectId
          }));
          console.log('[ProcessingScreen] Subscribing to sped_upload_started');
          
          if (spedType === 'xml') {
              // Subscribe to Unzip AND Process tasks
              ['unzip_upload', 'process_upload_xmls'].forEach(type => {
                  ws.send(JSON.stringify({
                    action: 'subscribe',
                    type: type,
                    projectId: projectId
                  }));
                  console.log(`[ProcessingScreen] Subscribing to ${type}`);
              });
          } else if (spedType === 'mixed') {
              // Modo misto: assinar logs de ambos os tipos + transição
              ['process_sped_icms', 'process_sped_pis', 'process_sped_mixed', 'process_sped_transition'].forEach(type => {
                  ws.send(JSON.stringify({
                    action: 'subscribe',
                    type: type,
                    projectId: projectId
                  }));
                  console.log(`[ProcessingScreen] Mixed mode - Subscribing to ${type}`);
              });
          } else {
              const messageType = spedType === 'icms' ? 'process_sped_icms' : 'process_sped_pis';
              const subscribeMsg = {
                action: 'subscribe',
                type: messageType,
                projectId: projectId
              };
              console.log('[ProcessingScreen] Subscribing:', subscribeMsg);
              ws.send(JSON.stringify(subscribeMsg));
          }
        };
        
        ws.onmessage = (event) => {
          try {
            const rawData = JSON.parse(event.data);
            
            // Ignorar mensagem de confirmação de inscrição
            if (rawData.success && rawData.message === 'Inscrição realizada com sucesso') return;
            // Ignorar mensagem de boas-vindas
            if (rawData.message?.type === 'info') return;
            
            // O servidor WebSocket encapsula as mensagens em {success: true, message: data}
            // Precisamos extrair a mensagem real
            const data = rawData.message || rawData;
            
            console.log('[ProcessingScreen] Received wrapped message:', rawData);
            console.log('[ProcessingScreen] Extracted message:', data);
            
            handleProgressUpdate(data);
          } catch (e) {
            console.error('[ProcessingScreen] Error parsing message:', e);
          }
        };
        
        ws.onerror = (error) => {
          console.error('[ProcessingScreen] WebSocket error:', error);
          setWsConnected(false);
        };
        
        ws.onclose = (event) => {
          console.log('[ProcessingScreen] WebSocket closed:', event.code, event.reason);
          setWsConnected(false);
          
          // Tentar reconectar se o processamento ainda não terminou
          if (overallStatus === 'processing') {
            reconnectTimeoutRef.current = setTimeout(() => {
              connectWebSocket();
            }, 3000);
          }
        };
        
      } catch (e) {
        console.error('[ProcessingScreen] Failed to connect WebSocket:', e);
        setWsConnected(false);
        setStatusMessage('Erro ao conectar com o servidor de logs');
      }
    };
    
    connectWebSocket();
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [projectId, spedType, handleProgressUpdate, overallStatus]);

  // Formatar números
  const fmt = (n) => n?.toLocaleString('pt-BR') || '0';

  const handleForceCancel = async () => {
    if (window.confirm("Deseja cancelar o processamento? Isso irá parar o processo e limpar o estado no servidor.")) {
        try {
            // Chamar endpoint para limpar arquivos no backend usando a instância api configurada
            console.log(`[ProcessingScreen] Cancelando processamento para ${projectId}...`);
            await api.delete(`/speds/cancel-processing/${projectId}`);
            console.log("[ProcessingScreen] Processamento cancelado no servidor com sucesso.");
        } catch (e) {
            console.error("[ProcessingScreen] Erro ao cancelar no servidor:", e);
        }

        // Limpar armazenamento local
        if (projectId) localStorage.removeItem(`ravena_active_upload_${projectId}`);
        
        // Fechar WebSocket
        if (wsRef.current) wsRef.current.close();

        // Callback de finalização
        if (onComplete) onComplete();

        // Forçar reload da página para garantir estado limpo se necessário
        // window.location.reload(); // Opcional, mas garante limpeza
    }
  };

  const handleReprocess = async () => {
    if (window.confirm("Deseja reprocessar os SPEDs já processados? Isso irá reprocessar todos os arquivos SPED que já foram processados anteriormente para este projeto.")) {
        try {
            console.log(`[ProcessingScreen] Iniciando reprocessamento para ${projectId}...`);
            
            // Chamar endpoint de reprocessamento
            const response = await api.post(`/speds/reprocess/${projectId}`);
            
            console.log("[ProcessingScreen] Reprocessamento iniciado:", response.data);
            
            // Resetar estados para mostrar novo processamento
            setTaskStates(tasks.map(t => ({ ...t, status: 'pending', progress: 0, records: 0, duration: null })));
            setGlobalProgress(0);
            setCurrentTaskIndex(-1);
            setOverallStatus('processing');
            setStatusMessage('Iniciando reprocessamento...');
            setTotalRecords(0);
            
            // Atualizar localStorage para manter sessão
            if (projectId) {
                localStorage.setItem(`ravena_active_upload_${projectId}`, JSON.stringify({
                    stage: 'processing',
                    operationId: null,
                    spedType: spedType,
                    timestamp: Date.now()
                }));
            }
            
        } catch (e) {
            console.error("[ProcessingScreen] Erro ao reprocessar:", e);
            const errorMsg = e.response?.data?.error || e.message || 'Erro desconhecido';
            alert(`Erro ao iniciar reprocessamento: ${errorMsg}`);
        }
    }
  };



  const getModalTitle = () => {
    if (spedType === 'xml') return 'Processando Arquivos XML';
    if (spedType === 'icms') return 'Processando SPED ICMS/IPI';
    if (spedType === 'pis') return 'Processando SPED PIS/COFINS';
    if (spedType === 'mixed') return `Processando SPED ${currentMixedPhase === 'icms' ? 'ICMS/IPI' : 'PIS/COFINS'}`;
    return 'Processando';
  };

  return (
    <ModalOverlay>
      <ModalContainer $maxWidth="480px" onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div className="header-left">
            <div className="header-icon">
              <GoSync className={overallStatus === 'processing' ? 'spin' : ''} />
            </div>
            <div>
              <h2>{getModalTitle()}</h2>
              <p>
                {spedType === 'xml' && "Descompactando, lendo e validando XMLs"}
                {spedType === 'icms' && "Processando e cruzando SPED ICMS/IPI"}
                {spedType === 'pis' && "Processando e cruzando SPED PIS/COFINS"}
                {spedType === 'mixed' && (
                  <>
                    Sequencial: <strong>{currentMixedPhase === 'icms' ? '① ICMS/IPI' : '② PIS/COFINS'}</strong>
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              title="Ver logs do worker em tempo real"
              onClick={() => setShowTerminal(true)}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.12)',
                color: '#8b949e',
                padding: '4px 8px',
                borderRadius: 6,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: '0.72em',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#10b981'; e.currentTarget.style.borderColor = '#10b98155'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#8b949e'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
            >
              <FiTerminal /> logs
            </button>
            <span style={{ 
              display: 'inline-flex', 
              alignItems: 'center',
              fontSize: '0.75em',
              color: wsConnected ? '#10b981' : '#ef4444',
              opacity: 0.7
            }}>
              {wsConnected ? <FiWifi /> : <FiWifiOff />}
            </span>
          </div>
        </ModalHeader>

        <ModalBody>
          <Container>
            <Header>
              <div className="global-progress" style={{ flex: 1 }}>
                <div className="progress-text">
                  <span>{globalProgress}%</span> concluído
                  {totalRecords > 0 && <small> • {fmt(totalRecords)} registros</small>}
                </div>
                <div className="progress-bar-container" style={{ width: '100%' }}>
                  <div className="progress-fill" style={{ width: `${globalProgress}%` }} />
                </div>
              </div>
            </Header>

            <TaskList>
              {taskStates.map((task, idx) => (
                <TaskItem key={task.id} className={task.status}>
                  <div className={`task-icon ${task.status}`}>
                    {task.status === 'pending' && <FiClock />}
                    {task.status === 'active' && <FiLoader className="spin" />}
                    {task.status === 'completed' && <FiCheckCircle />}
                    {task.status === 'error' && <FiAlertCircle />}
                  </div>
                  <div className="task-info">
                    <div className="task-header">
                      <span className="task-name">{task.name}</span>
                      <span className={`task-status ${task.status}`}>
                        {task.status === 'pending' && 'Aguardando'}
                        {task.status === 'active' && 'Processando...'}
                        {task.status === 'completed' && 'Concluído'}
                        {task.status === 'error' && 'Erro'}
                      </span>
                    </div>
                    <div className={`task-progress`}>
                      <div 
                        className={`progress-fill ${task.status}`} 
                        style={{ width: `${task.progress}%` }} 
                      />
                    </div>
                    {(task.status === 'completed' || task.status === 'active') && (
                      <div className="task-details">
                         {/* Se a tarefa estiver ativa, mostra a mensagem de status (progresso detalhado) ou contagem parcial */}
                         {task.status === 'active' 
                            ? <span>
                                {statusMessage || (
                                    <span><GoDatabase /> {fmt(task.records || 0)} {getTaskLabel(task.id)}</span>
                                )}
                              </span>
                            : <span>
                                <GoDatabase /> {fmt(task.records || 0)} {getTaskLabel(task.id)}
                              </span>
                         }
                        {task.duration && <span><FiClock /> {task.duration}s</span>}
                      </div>
                    )}
                  </div>
                </TaskItem>
              ))}
            </TaskList>

            {overallStatus === 'success' && (
              <StatusMessage className="success">
                <div className="status-icon success"><GoCheck /></div>
                <div className="status-text">
                  <h4>Processamento Concluído!</h4>
                  <p>
                    {fmt(totalRecords)} registros extraídos e indexados.
                  </p>
                </div>
                <div className="status-action">
                  <button onClick={onComplete}>Ver Arquivos</button>
                </div>
              </StatusMessage>
            )}

            {overallStatus === 'error' && (
              <StatusMessage className="error">
                <div className="status-icon error"><GoX /></div>
                <div className="status-text">
                  <h4>Erro no Processamento</h4>
                  <p>{statusMessage}</p>
                </div>
              </StatusMessage>
            )}
          </Container>
        </ModalBody>

        <ModalFooter $justify="space-between">
          <ModalButton className="danger" onClick={handleForceCancel} title="Cancelar processamento">
            <FiX /> Cancelar
          </ModalButton>
          <ModalButton className="info" onClick={handleReprocess} title="Reprocessar SPEDs">
            <FiRefreshCw /> Reprocessar
          </ModalButton>
        </ModalFooter>
      </ModalContainer>

      {showTerminal && (
        <WorkerLogTerminal
          projectId={projectId}
          onClose={() => setShowTerminal(false)}
        />
      )}
    </ModalOverlay>
  );
};

export default ProcessingScreen;
